<template>
  <div class="bg-white rounded-2xl p-7 border border-[#1c4848]/10 shadow-sm" :class="[wrapperClass]">
    <div class="flex items-start justify-between mb-6 gap-4">
      <div class="min-w-0">
        <h3 class="text-[20px] font-medium tracking-tight text-[#1c4848]">{{ current.title }}</h3>
        <p class="text-[15px] text-[#1c4848]/60 mt-1">{{ current.description }}</p>
        <p v-if="current.hint" class="text-[13px] text-[#1c4848]/40 mt-0.5">{{ current.hint }}</p>
      </div>

      <!-- Which graph -->
      <Listbox v-model="selected">
        <div class="relative shrink-0">
          <ListboxButton
            class="flex items-center gap-2.5 bg-white text-[#1c4848] text-[15px] font-medium rounded-xl pl-3.5 pr-3 py-2.5 border border-[#1c4848]/15 shadow-sm hover:border-[#1c4848]/35 transition-colors"
          >
            <span class="w-2.5 h-2.5 rounded-full shrink-0" :style="{ backgroundColor: current.color || palette[0] }" />
            <span>{{ current.title }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-[#1c4848]/50"><path d="m6 9 6 6 6-6"/></svg>
          </ListboxButton>
          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="opacity-0 -translate-y-1"
            leave-active-class="transition duration-75 ease-in"
            leave-to-class="opacity-0 -translate-y-1"
          >
            <ListboxOptions
              class="absolute right-0 z-20 mt-2 w-[470px] bg-white border border-[#1c4848]/10 rounded-2xl shadow-[0_20px_50px_-12px_rgba(20,53,53,0.35)] overflow-hidden outline-none"
            >
              <li class="px-4 pt-3 pb-2 text-[12px] font-medium tracking-wide text-[#1c4848]/45 border-b border-[#1c4848]/[0.08]">Show on the graph</li>
              <ListboxOption v-for="view in views" :key="view.key" :value="view.key" v-slot="{ active, selected: isSelected }" as="template">
                <li
                  class="flex items-center gap-3.5 px-4 py-3.5 cursor-pointer border-b border-[#1c4848]/[0.08] last:border-b-0 transition-colors"
                  :class="[active ? 'bg-[#f3f7f6]' : '', isSelected ? 'bg-[#eef5f3]' : '']"
                >
                  <span class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" :style="{ backgroundColor: hexToRgba(view.color || palette[0], 0.12), color: view.color || palette[0] }">
                    <svg v-if="view.icon === 'bars'" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 20V10M12 20V4M19 20v-7"/></svg>
                    <svg v-else-if="view.icon === 'people'" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    <svg v-else-if="view.icon === 'card'" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>
                    <svg v-else-if="view.icon === 'grid'" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/></svg>
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block text-[15px] font-semibold text-[#1c4848] leading-tight">{{ view.title }}</span>
                    <span class="block text-[13px] text-[#1c4848]/55 mt-1 truncate">{{ view.description }}</span>
                  </span>
                  <svg v-if="isSelected" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1c4848" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="shrink-0"><path d="M20 6 9 17l-5-5"/></svg>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
    </div>

    <p v-if="!hasData" class="text-base text-[#1c4848]/40 py-16 text-center">
      Nothing recorded in this period yet.
    </p>

    <div v-else class="w-full h-[380px]" style="min-height: 380px">
      <Chart type="line" :data="chartData" :options="chartOptions" />
    </div>

    <div v-if="hasData" class="mt-5 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
      <div v-for="line in current.series" :key="line.key" class="rounded-2xl bg-[#fafbfc] px-4 py-3">
        <p class="text-[13px] font-semibold text-[#1c4848]/45 truncate">{{ line.tileName || line.name }}</p>
        <p class="text-[28px] font-semibold text-[#1c4848] mt-1 tabular-nums">
          {{ formatNumber(line.agg === 'last' ? last(line.data) : sum(line.data)) }}
          <span class="text-[13px] font-medium text-[#1c4848]/45 ml-1">{{ current.unit || 'total' }}</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'vue-chartjs';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, LineController, BarController, Filler, Title, Tooltip, Legend);

const props = defineProps({
  /**
   * [{ key, title, description, unit?, labels: [], series: [{ name, key, data }] }]
   * One entry per graph the dropdown can switch to.
   */
  views: { type: Array, default: () => [] },
  modelValue: { type: String, default: '' },
  wrapperClass: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

const selected = ref(props.modelValue || props.views[0]?.key || '');
watch(() => props.modelValue, (v) => { if (v) selected.value = v; });
watch(selected, (v) => emit('update:modelValue', v));

const current = computed(() => props.views.find((v) => v.key === selected.value) || props.views[0] || { title: '', description: '', labels: [], series: [] });

const palette = ['#1c4848', '#ecd79f', '#7a9e9e', '#c9a227'];

const hasData = computed(
  () => (current.value.labels || []).length > 0 && (current.value.series || []).some((line) => (line.data || []).some((point) => point > 0)),
);

function hexToRgba(hex, alpha) {
  const value = hex.replace('#', '');
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const chartData = computed(() => ({
  labels: current.value.labels,
  datasets: (current.value.series || []).map((line, index) => {
    const color = line.color || palette[index % palette.length];
    return {
      label: line.name,
      data: line.data,
      yAxisID: line.axis === 'right' ? 'y1' : 'y',
      type: line.bars ? 'bar' : 'line',
      borderRadius: line.bars ? 4 : 0,
      borderColor: color,
      pointBackgroundColor: color,
      pointRadius: 0,
      pointHoverRadius: 4,
      borderWidth: line.bars ? 0 : 2,
      tension: 0.4,
      fill: !line.bars && index === 0,
      backgroundColor: line.bars ? hexToRgba(color, 0.85) : (ctx) => {
        const { chart } = ctx;
        if (!chart.chartArea) return hexToRgba(color, 0.08);
        const gradient = chart.ctx.createLinearGradient(0, chart.chartArea.top, 0, chart.chartArea.bottom);
        gradient.addColorStop(0, hexToRgba(color, 0.22));
        gradient.addColorStop(1, hexToRgba(color, 0));
        return gradient;
      },
    };
  }),
}));

const hasRight = computed(() => (current.value.series || []).some((l) => l.axis === 'right'));
const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, pointStyle: 'circle', color: '#1c4848', font: { family: "'Plus Jakarta Sans', sans-serif", size: 11, weight: '600' } },
    },
    tooltip: {
      backgroundColor: '#1c4848',
      padding: 10,
      cornerRadius: 8,
      titleFont: { family: "'Plus Jakarta Sans', sans-serif", size: 11 },
      bodyFont: { family: "'Plus Jakarta Sans', sans-serif", size: 11 },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { color: '#9ca3af', maxTicksLimit: 8, font: { family: "'Plus Jakarta Sans', sans-serif", size: 10 } } },
    y: { beginAtZero: true, grid: { color: 'rgba(28,72,72,0.05)' }, ticks: { color: '#9ca3af', precision: 0, font: { family: "'Plus Jakarta Sans', sans-serif", size: 11 } } },
  },
};
const chartOptions = computed(() => ({
  ...baseOptions,
  scales: {
    ...baseOptions.scales,
    y1: { display: hasRight.value, position: 'right', beginAtZero: true, grid: { display: false }, ticks: { color: '#9ca3af', precision: 0, font: { family: "'Plus Jakarta Sans', sans-serif", size: 11 } } },
  },
}));

const sum = (data) => (data || []).reduce((a, b) => a + (Number(b) || 0), 0);
/** For running totals the period figure is the last point, not the sum of the curve. */
const last = (data) => Number((data || [])[(data || []).length - 1] || 0);
const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value ?? 0);
</script>
